import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  BookOpenIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  BookmarkIcon,
  PlusIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
import clsx from 'clsx'

import { researchLiteratureAPI, type LiteratureDiscoveryRequest } from '@/api/researchLiterature'
import { citationLibraryAPI } from '@/api/citationLibrary'
import type { CitationPaper } from '@/api/citations'
import { generateBibtexEntry, generateBibtexKey } from '@/utils/generateBibtex'
import { logError } from '@utils/logger'
import { useDocumentStore } from '@stores/documentStore'
import { useQueryClient } from '@tanstack/react-query'
import PaperDetailModal from '@components/research/PaperDetailModal'
import { FeatureNotice } from '@components/common/FeatureNotice'

const SearchResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentDocument } = useDocumentStore()
  
  const initialQuery = searchParams.get('q') || ''
  const initialCategory = searchParams.get('category') || 'research'
  
  const [query, setQuery] = useState(initialQuery)
  const [papers, setPapers] = useState<CitationPaper[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedPapers, setSelectedPapers] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState({
    yearFrom: searchParams.get('yearFrom') || '',
    yearTo: searchParams.get('yearTo') || '',
    maxResults: parseInt(searchParams.get('maxResults') || '50'),
    customInstructions: searchParams.get('customInstructions') || '',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedPaper, setSelectedPaper] = useState<CitationPaper | null>(null)
  const [subscriptionRequired, setSubscriptionRequired] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const queryToUse = searchQuery || query
    if (!queryToUse.trim()) {
      return
    }

    setIsSearching(true)
    setPapers([])
    setSelectedPapers(new Set())

    // Update URL params
    const params = new URLSearchParams()
    params.set('q', queryToUse)
    params.set('category', 'research')
    if (filters.yearFrom) params.set('yearFrom', filters.yearFrom)
    if (filters.yearTo) params.set('yearTo', filters.yearTo)
    if (filters.maxResults) params.set('maxResults', filters.maxResults.toString())
    if (filters.customInstructions) params.set('customInstructions', filters.customInstructions)
    setSearchParams(params, { replace: true })

    try {
      const request: LiteratureDiscoveryRequest = {
        query: queryToUse,
        max_results: filters.maxResults,
        year_from: filters.yearFrom ? parseInt(filters.yearFrom) : undefined,
        year_to: filters.yearTo ? parseInt(filters.yearTo) : undefined,
        custom_instructions: filters.customInstructions || undefined,
      }

      const response = await researchLiteratureAPI.discoverLiterature(request)
      setPapers(response.papers)
      setSubscriptionRequired(false) // Clear subscription error state on success
      if (response.papers.length > 0) {
      }
      
      // Invalidate token usage queries
      if (currentDocument?.id) {
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', currentDocument.id] })
      }
      queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })
    } catch (error: any) {
      logError('Error discovering literature:', error)
      
      // Check if this is a subscription-required error
      const isSubscriptionError = error.response?.status === 402 && 
        error.response?.data?.detail?.required_subscription
      
      if (isSubscriptionError) {
        // Treat as daily credit exhaustion / free tier limit
        setSubscriptionRequired(true)
        setPapers([]) // Clear papers to show empty state
        return
      }
      
      // For other errors, show toast
      setSubscriptionRequired(false)
      let errorMessage = 'Failed to discover literature'
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail
        if (typeof detail === 'string') {
          errorMessage = detail
        } else if (typeof detail === 'object') {
          errorMessage = detail.message || detail.error || 'Failed to discover literature'
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      toast.error(errorMessage || 'Something went wrong. Please contact the administrator.')
    } finally {
      setIsSearching(false)
    }
  }, [query, filters, setSearchParams, currentDocument?.id, queryClient])

  // Auto-search if query is provided in URL
  useEffect(() => {
    if (initialQuery && initialCategory === 'research') {
      handleSearch(initialQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const togglePaperSelection = (paperTitle: string) => {
    setSelectedPapers(prev => {
      const next = new Set(prev)
      if (next.has(paperTitle)) {
        next.delete(paperTitle)
      } else {
        next.add(paperTitle)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedPapers.size === papers.length) {
      setSelectedPapers(new Set())
    } else {
      setSelectedPapers(new Set(papers.map(p => p.title)))
    }
  }

  const handleSaveToLibrary = async () => {
    if (!currentDocument?.id) {
      return
    }

    const papersToProcess = papers.filter(p => selectedPapers.has(p.title))

    if (papersToProcess.length === 0) {
      return
    }

    try {
      let savedCount = 0
      for (const paper of papersToProcess) {
        try {
          const bibtexKey = generateBibtexKey(paper)
          const bibtexEntry = generateBibtexEntry(paper, bibtexKey)

          await citationLibraryAPI.saveCitation(currentDocument.id, {
            title: paper.title,
            authors: paper.authors,
            year: paper.year,
            doi: paper.doi,
            pmid: paper.pmid,
            arxiv_id: paper.arxiv_id,
            abstract: paper.abstract,
            venue: paper.venue,
            url: paper.url,
            bibtex_key: bibtexKey,
            bibtex_entry: bibtexEntry,
          })
          savedCount++
        } catch (error) {
          logError(`Error saving paper "${paper.title}":`, error)
        }
      }

      setSelectedPapers(new Set())
    } catch (error: any) {
      logError('Error saving papers:', error)
    }
  }

  const handleSaveSinglePaper = async (paper: CitationPaper) => {
    if (!currentDocument?.id) {
      return
    }

    try {
      const bibtexKey = generateBibtexKey(paper)
      const bibtexEntry = generateBibtexEntry(paper, bibtexKey)

      await citationLibraryAPI.saveCitation(currentDocument.id, {
        title: paper.title,
        authors: paper.authors,
        year: paper.year,
        doi: paper.doi,
        pmid: paper.pmid,
        arxiv_id: paper.arxiv_id,
        abstract: paper.abstract,
        venue: paper.venue,
        url: paper.url,
        bibtex_key: bibtexKey,
        bibtex_entry: bibtexEntry,
      })

    } catch (error: any) {
      logError('Error saving paper:', error)
    }
  }

  const handlePaperClick = (paper: CitationPaper, e: React.MouseEvent) => {
    // Don't open modal if clicking on buttons or links
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]')
    ) {
      return
    }
    setSelectedPaper(paper)
    setIsModalOpen(true)
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/discover')}
            className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Discover</span>
          </button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl shadow-lg">
              <BookOpenIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                Search Results
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Academic literature search
              </p>
            </div>
          </div>
        </div>

        {/* Feature Notice - Show when subscription is required */}
        {subscriptionRequired && (
          <div className="mb-6">
            <FeatureNotice
              type="subscription"
              message="Literature discovery is available for subscribed users."
              onDismiss={() => setSubscriptionRequired(false)}
            />
          </div>
        )}

        {/* Search Bar with Filter */}
        <div className="mb-8">
          <div className="relative max-w-3xl">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search for research papers, e.g., 'Machine learning for combustion simulation'"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:border-teal-500 dark:focus:border-teal-500 text-slate-900 dark:text-white placeholder-slate-400"
                  disabled={isSearching}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'px-4 py-4 rounded-xl border-2 transition-all',
                  showFilters
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                )}
                title="Filters"
              >
                <FunnelIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleSearch()}
                disabled={!query.trim() || isSearching}
                className={clsx(
                  'px-6 py-4 rounded-xl font-semibold transition-all flex items-center gap-2',
                  !query.trim() || isSearching
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:shadow-lg'
                )}
              >
                {isSearching ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="w-5 h-5" />
                    Search
                  </>
                )}
              </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 p-6 bg-white dark:bg-slate-800 rounded-xl border-2 border-slate-200 dark:border-slate-700 space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Year From
                    </label>
                    <input
                      type="number"
                      value={filters.yearFrom}
                      onChange={(e) => setFilters({ ...filters, yearFrom: e.target.value })}
                      placeholder="e.g., 2020"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Year To
                    </label>
                    <input
                      type="number"
                      value={filters.yearTo}
                      onChange={(e) => setFilters({ ...filters, yearTo: e.target.value })}
                      placeholder="e.g., 2024"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Max Results
                  </label>
                  <input
                    type="number"
                    value={filters.maxResults}
                    onChange={(e) => setFilters({ ...filters, maxResults: parseInt(e.target.value) || 50 })}
                    min={1}
                    max={100}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Custom Instructions (Optional)
                  </label>
                  <textarea
                    value={filters.customInstructions}
                    onChange={(e) => setFilters({ ...filters, customInstructions: e.target.value })}
                    placeholder="e.g., Focus on recent deep learning papers, prioritize papers from top-tier conferences..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                  />
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Results */}
        {isSearching && (
          <div className="text-center py-12">
            <ArrowPathIcon className="w-12 h-12 text-teal-500 mx-auto mb-4 animate-spin" />
            <p className="text-slate-600 dark:text-slate-400">Searching for relevant papers...</p>
          </div>
        )}

        {!isSearching && papers.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Found {papers.length} papers
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-teal-600 dark:text-teal-400 hover:underline"
                >
                  {selectedPapers.size === papers.length ? 'Deselect All' : 'Select All'}
                </button>
                {currentDocument?.id && selectedPapers.size > 0 && (
                  <button
                    onClick={handleSaveToLibrary}
                    className="flex items-center gap-2 text-sm text-teal-600 dark:text-teal-400 hover:underline"
                  >
                    <BookmarkIcon className="w-4 h-4" />
                    Save {selectedPapers.size} to Library
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {papers.map((paper, index) => {
                const isSelected = selectedPapers.has(paper.title)
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={clsx(
                      'p-6 rounded-xl border-2 transition-all cursor-pointer',
                      isSelected
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                    )}
                    onClick={(e) => handlePaperClick(paper, e)}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          togglePaperSelection(paper.title)
                        }}
                        className="mt-1"
                      >
                        {isSelected ? (
                          <CheckIconSolid className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                        ) : (
                          <div className="w-6 h-6 border-2 border-slate-300 dark:border-slate-600 rounded hover:border-teal-500 transition-colors" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-2 text-lg">
                          {paper.title}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          {paper.authors.join(', ')}
                          {paper.year && ` • ${paper.year}`}
                          {paper.venue && ` • ${paper.venue}`}
                        </p>
                        {paper.abstract && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-3 mb-3">
                            {paper.abstract}
                          </p>
                        )}
                        {paper.relevance_score && (
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-3">
                            <AcademicCapIcon className="w-4 h-4" />
                            Relevance: {(paper.relevance_score * 100).toFixed(0)}%
                          </div>
                        )}
                        <div className="flex items-center gap-3 flex-wrap">
                          {paper.doi && (
                            <a
                              href={`https://doi.org/${paper.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-teal-600 dark:text-teal-400 hover:underline"
                            >
                              View Paper
                            </a>
                          )}
                          {currentDocument?.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSaveSinglePaper(paper)
                              }}
                              className="flex items-center gap-1 text-sm text-teal-600 dark:text-teal-400 hover:underline"
                            >
                              <PlusIcon className="w-4 h-4" />
                              Add to Library
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isSearching && papers.length === 0 && query && !subscriptionRequired && (
          <div className="text-center py-12">
            <BookOpenIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">
              No papers found. Try adjusting your search query or filters.
            </p>
          </div>
        )}

        {!isSearching && papers.length === 0 && !query && (
          <div className="text-center py-12">
            <BookOpenIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">
              Enter a search query to discover relevant research papers
            </p>
          </div>
        )}

        {/* Paper Detail Modal */}
        <PaperDetailModal
          paper={selectedPaper}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedPaper(null)
          }}
          onSaveToLibrary={currentDocument?.id ? handleSaveSinglePaper : undefined}
          showSaveButton={!!currentDocument?.id}
        />
      </div>
    </div>
  )
}

export default SearchResultsPage

